import { t } from '@trimble/i18n';
import type { TreeNodeDefinition } from '@trimble/ui-components/solidjs';

import { isExtractedFeatureTreeNodeKind } from './fx-workspace-tree-nodes';
import type { FxWorkspaceTreeItem } from './fx-workspace-tree.types';
import { filterProjectDataNodes } from './project-data-filtering';

export type FxReferencesFilterMode = 'active' | 'workspace';

export const FX_REFERENCES_FILTER_OPTIONS: () => Array<{
  key: FxReferencesFilterMode;
  label: string;
}> = () => [
  {
    key: 'active',
    label: t('mobileMapping.fxWorkspace.references.filter.activeData'),
  },
  {
    key: 'workspace',
    label: t('mobileMapping.fxWorkspace.references.filter.workspaceData'),
  },
];

export function isReferencesTreeNodeKind(
  kind: FxWorkspaceTreeItem['kind'] | undefined
): boolean {
  return (
    kind === 'references' ||
    kind === 'referenceCategory' ||
    kind === 'referenceMission' ||
    kind === 'referenceRuns' ||
    kind === 'referenceFolder' ||
    kind === 'reference'
  );
}

export function applyFxReferencesFilter(
  nodes: TreeNodeDefinition<FxWorkspaceTreeItem>[],
  isMatch: (node: TreeNodeDefinition<FxWorkspaceTreeItem>) => boolean
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const extracted = nodes.filter((node) =>
    isExtractedFeatureTreeNodeKind(node.data?.kind)
  );
  const references = nodes.filter((node) =>
    isReferencesTreeNodeKind(node.data?.kind)
  );
  const filteredReferences = filterProjectDataNodes(
    references,
    'active',
    '',
    isMatch
  ).nodes;
  const directlyVisible = filteredReferences.filter(
    (node) => node.data?.kind !== 'reference' || isMatch(node)
  );
  const directlyVisibleIds = new Set(directlyVisible.map((node) => node.id));
  // A map workspace row is itself a reference. Keep that row when a saved
  // layer is nested under it, so the layer keeps its parent in the tree.
  const visibleReferences = filteredReferences.filter((node) => {
    if (node.data?.kind !== 'reference' || isMatch(node)) {
      return true;
    }

    return filteredReferences.some(
      (child) => child.parentId === node.id && directlyVisibleIds.has(child.id)
    );
  });

  return [...extracted, ...visibleReferences];
}
